import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../context/AuthContext";
import {
  ConfigProvider,
  Layout,
  Card,
  Avatar,
  Typography,
  Tag,
  Button,
  Select,
  Input,
  DatePicker,
  Checkbox,
  Space,
  Skeleton,
  Empty,
  Alert,
  List,
  message,
} from "antd";
import {
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import "./ExitClearanceApproval.css";

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Config
// NOTE: import path is "../context/AuthContext" — adjust if this file moves.
// Edit COMPANY_NAME to match your letterhead.
// ---------------------------------------------------------------------------

const COMPANY_NAME = "Baashyaam Constructions Pvt Ltd";
const EMPLOYEES_PATH = "/api/emp/employees/";
const EXIT_PROCESS_LIST_PATH = "/api/exitprocess/exit-process/";
const EXIT_PROCESS_DETAIL_PATH = (pk) => `/api/exitprocess/exit-process/${pk}/`;

const SECTION_PATCH_PATH = {
  department_head: (pk) => `/api/exitprocess/${pk}/department-head/`,
  it: (pk) => `/api/exitprocess/${pk}/it/`,
  finance: (pk) => `/api/exitprocess/${pk}/finance/`,
  hr: (pk) => `/api/exitprocess/${pk}/hr/`,
};

// Matches ClearanceType / model field names in exitprocess/models.py exactly.
const ITEM_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "COMPLETED", label: "Completed" },
];

const RETURN_STATUS_OPTIONS = [
  { value: "NOT_RETURNED", label: "Not Returned" },
  { value: "RETURNED", label: "Returned" },
  { value: "NOT_APPLICABLE", label: "Not Applicable" },
];

const CLEARANCE_STATUS_META = {
  PENDING: { color: "gold", label: "Pending" },
  IN_PROGRESS: { color: "blue", label: "In Progress" },
  COMPLETED: { color: "success", label: "Completed" },
  REJECTED: { color: "error", label: "Rejected" },
};

// One entry per BaseClearance subclass. `itemMode` drives which columns the
// items table renders (see ClearanceItemsTable below).
const ROLE_META = [
  {
    key: "department_head",
    clearanceKey: "department_head_clearance",
    label: "Department Head",
    itemMode: "status", // particulars + Pending/Completed dropdown
    statusOptions: ITEM_STATUS_OPTIONS,
    hasHandover: true,
  },
  {
    key: "it",
    clearanceKey: "it_clearance",
    label: "IT",
    itemMode: "return_status", // particulars + Returned/Not Returned/N-A + remarks
    statusOptions: RETURN_STATUS_OPTIONS,
  },
  {
    key: "finance",
    clearanceKey: "finance_clearance",
    label: "Finance",
    itemMode: "amount", // item name + amount to be deducted + remarks
  },
  {
    key: "hr",
    clearanceKey: "hr_clearance",
    label: "HR",
    itemMode: "return_status",
    statusOptions: RETURN_STATUS_OPTIONS,
    hasExitInterview: true,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function displayName(person) {
  if (!person) return "";
  const parts = [person.salutation, person.first_name, person.last_name].filter(Boolean);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function initials(person) {
  const name = `${person?.first_name || ""} ${person?.last_name || ""}`.trim();
  if (!name) return <UserOutlined />;
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

// ADAPT: point this at wherever the logged-in employee's own id lives on
// your AuthContext (it only carried the access token before).
function useCurrentEmployeeId(authData) {
  return authData?.employee?.id ?? authData?.employeeId ?? null;
}

function resolveMyRole(detail, employeeId) {
  if (!detail || !employeeId) return null;
  return (
    ROLE_META.find((role) => detail[role.clearanceKey]?.approver?.id === employeeId) || null
  );
}

let tempIdSeq = 0;
function nextTempId() {
  tempIdSeq += 1;
  return `new-${tempIdSeq}`;
}

// ---------------------------------------------------------------------------
// Items table — columns change per role's itemMode
// ---------------------------------------------------------------------------

function ClearanceItemsTable({ role, items, onChange, readOnly }) {
  function updateItem(rowKey, patch) {
    onChange(items.map((it) => (rowKey(it) === rowKey(it) && it === it ? it : it)));
  }

  function patchAt(index, patch) {
    const next = items.slice();
    next[index] = { ...next[index], ...patch };
    onChange(next);
  }

  function removeAt(index) {
    const next = items.slice();
    next.splice(index, 1);
    onChange(renumber(next));
  }

  function addRow() {
    const base =
      role.itemMode === "amount"
        ? { _tempId: nextTempId(), item_name: "", amount_to_be_deducted: 0, remarks: "" }
        : role.itemMode === "status"
        ? { _tempId: nextTempId(), particulars: "", status: "PENDING" }
        : { _tempId: nextTempId(), particulars: "", status: "NOT_RETURNED", remarks: "" };
    onChange(renumber([...items, base]));
  }

  function renumber(list) {
    return list.map((it, i) => ({ ...it, sr_no: i + 1 }));
  }

  const isAmount = role.itemMode === "amount";
  const hasRemarks = role.itemMode !== "status";

  return (
    <div className="paper-table">
      <div className="paper-table-head">
        <span className="pt-col pt-col-sr">SR. NO.</span>
        <span className="pt-col pt-col-main">{isAmount ? "ITEM NAME" : "LIST OF ITEMS"}</span>
        <span className="pt-col pt-col-status">{isAmount ? "AMOUNT" : "STATUS"}</span>
        {!readOnly && <span className="pt-col pt-col-del" />}
      </div>

      {items.length === 0 && (
        <div className="paper-table-empty">
          <Text type="secondary">No items yet.</Text>
        </div>
      )}

      {items.map((item, index) => {
        const rowKey = item.id ?? item._tempId;
        return (
          <div className="paper-table-row" key={rowKey}>
            <span className="pt-col pt-col-sr">{index + 1}</span>

            <span className="pt-col pt-col-main">
              <Input
                variant="outlined"
                className="paper-input"
                disabled={readOnly}
                value={isAmount ? item.item_name : item.particulars}
                placeholder={isAmount ? "e.g. Loans / Advance" : "e.g. Assets (Software, Hardware...)"}
                onChange={(e) =>
                  patchAt(index, isAmount ? { item_name: e.target.value } : { particulars: e.target.value })
                }
              />
            </span>

            <span className="pt-col pt-col-status">
              {isAmount ? (
                <Input
                  type="number"
                  className="paper-input paper-input-amount"
                  disabled={readOnly}
                  value={item.amount_to_be_deducted}
                  prefix="₹"
                  onChange={(e) => patchAt(index, { amount_to_be_deducted: e.target.value })}
                />
              ) : (
                <Select
                  className="paper-select"
                  disabled={readOnly}
                  value={item.status}
                  options={role.statusOptions}
                  onChange={(value) => patchAt(index, { status: value })}
                  variant="borderless"
                />
              )}
            </span>

            {!readOnly && (
              <span className="pt-col pt-col-del">
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeAt(index)}
                />
              </span>
            )}
          </div>
        );
      })}

      {hasRemarks && items.length > 0 && (
        <div className="paper-remarks-hint">
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Tip: use the overall remarks box below for notes on individual items.
          </Text>
        </div>
      )}

      {!readOnly && (
        <Button type="dashed" icon={<PlusOutlined />} className="add-item-btn" onClick={addRow}>
          Add item
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The paper form itself — only rendered for the logged-in user's own role
// ---------------------------------------------------------------------------

function ClearanceFormPaper({ role, section, exitProcessId, employee, resignationDate, lastWorkingDay, employees, onSaved, api, headers, currentEmployee }) {
  const [items, setItems] = useState(() => (section?.items || []).map((i) => ({ ...i })));
  const [remarks, setRemarks] = useState(section?.remarks || "");
  const [signatureDate, setSignatureDate] = useState(
    section?.signature_date ? dayjs(section.signature_date) : dayjs()
  );
  const [handedOverTo, setHandedOverTo] = useState(section?.handed_over_to?.id ?? null);
  const [exitInterviewDone, setExitInterviewDone] = useState(!!section?.exit_interview_completed);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems((section?.items || []).map((i) => ({ ...i })));
    setRemarks(section?.remarks || "");
    setSignatureDate(section?.signature_date ? dayjs(section.signature_date) : dayjs());
    setHandedOverTo(section?.handed_over_to?.id ?? null);
    setExitInterviewDone(!!section?.exit_interview_completed);
  }, [section]);

  const status = section?.status || "PENDING";
  const alreadyDecided = status === "COMPLETED" || status === "REJECTED";

  async function submit(nextStatus) {
    if (nextStatus === "REJECTED" && !rejectReason.trim()) {
      message.error("Add a reason before rejecting.");
      return;
    }
    setSaving(true);
    try {
      const itemsPayload = items.map((it) => {
        const clean = { ...it };
        delete clean._tempId;
        // Omit id on brand-new rows so the backend nested serializer treats
        // them as creates rather than updates.
        if (typeof clean.id !== "number") delete clean.id;
        if (role.itemMode === "amount") {
          clean.amount_to_be_deducted = Number(clean.amount_to_be_deducted) || 0;
        }
        return clean;
      });

      const payload = {
        status: nextStatus,
        remarks: nextStatus === "REJECTED" ? rejectReason.trim() : remarks,
        signature_date: signatureDate ? signatureDate.format("YYYY-MM-DD") : null,
        items: itemsPayload,
      };
      if (role.hasHandover) payload.handed_over_to = handedOverTo;
      if (role.hasExitInterview) payload.exit_interview_completed = exitInterviewDone;

      await api.patch(SECTION_PATCH_PATH[role.key](exitProcessId), payload, { headers });
      message.success(nextStatus === "COMPLETED" ? "Clearance completed." : "Clearance rejected.");
      onSaved();
    } catch (err) {
      console.error("Error saving clearance section:", err);
      message.error(err.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="paper-sheet">
      <div className="paper-letterhead">
        <div className="paper-company">{COMPANY_NAME}</div>
        <div className="paper-subtitle">Employee Clearance Form</div>
      </div>
      <div className="paper-hr" />

      <div className="paper-employee-row">
        <div className="paper-employee-field">
          <Text className="paper-field-label">Employee Name</Text>
          <Text className="paper-employee-value">{displayName(employee) || "—"}</Text>
        </div>
        <div className="paper-employee-field">
          <Text className="paper-field-label">Employee No.</Text>
          <Text className="paper-employee-value">{employee?.emp_code || "—"}</Text>
        </div>
        <div className="paper-employee-field">
          <Text className="paper-field-label">Resignation Date</Text>
          <Text className="paper-employee-value">{formatDate(resignationDate)}</Text>
        </div>
        <div className="paper-employee-field">
          <Text className="paper-field-label">Last Working Day</Text>
          <Text className="paper-employee-value">{formatDate(lastWorkingDay)}</Text>
        </div>
      </div>

      <div className="paper-section-head">
        <span className="paper-section-title">CLEARANCE FROM — {role.label.toUpperCase()}</span>
        <Tag color={CLEARANCE_STATUS_META[status]?.color}>{CLEARANCE_STATUS_META[status]?.label}</Tag>
      </div>

      {alreadyDecided ? (
        <div className="paper-done-banner">
          <CheckCircleFilled style={{ color: status === "COMPLETED" ? "#2f5d50" : "#c0392b" }} />
          <Text>
            You {status === "COMPLETED" ? "completed" : "rejected"} this clearance on{" "}
            {formatDate(section?.cleared_at)}.
          </Text>
        </div>
      ) : null}

      <ClearanceItemsTable
        role={role}
        items={items}
        onChange={setItems}
        readOnly={alreadyDecided}
      />

      {role.hasHandover && (
        <div className="paper-extra-field">
          <Text className="paper-field-label">Handed over to</Text>
          <Select
            className="paper-select-wide"
            showSearch
            allowClear
            disabled={alreadyDecided}
            placeholder="Select employee who received the handover"
            value={handedOverTo}
            onChange={setHandedOverTo}
            options={employees.map((e) => ({ value: e.id, label: displayName(e) }))}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </div>
      )}

      {/* {role.hasExitInterview && (
        <div className="paper-extra-field">
          <Checkbox
            checked={exitInterviewDone}
            disabled={alreadyDecided}
            onChange={(e) => setExitInterviewDone(e.target.checked)}
          >
            Exit interview completed
          </Checkbox>
        </div>
      )} */}

      <div className="paper-remarks-field">
        <Text className="paper-field-label">Remarks</Text>
        <Input.TextArea
          rows={2}
          disabled={alreadyDecided}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any notes for HR / the employee"
        />
      </div>

      {!alreadyDecided && rejecting && (
        <div className="paper-remarks-field paper-reject-field">
          <Text type="danger" className="paper-field-label">Reason for rejection (required)</Text>
          <Input.TextArea
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </div>
      )}

      <div className="paper-signature-row">
        <div className="paper-signature-block">
          <Text className="paper-field-label">Signature ({role.label}):</Text>
          <div className="paper-signature-line">{displayName(currentEmployee) || "—"}</div>
        </div>
        <div className="paper-date-block">
          <Text className="paper-field-label">Date</Text>
          <DatePicker
            value={signatureDate}
            onChange={setSignatureDate}
            disabled={alreadyDecided}
            format="DD MMM YYYY"
            className="paper-datepicker"
          />
        </div>
      </div>

      {!alreadyDecided && (
        <div className="paper-actions">
          {!rejecting ? (
            <>
              <Button danger onClick={() => setRejecting(true)}>Reject</Button>
              <Button type="primary" loading={saving} onClick={() => submit("COMPLETED")}>
                Complete Clearance
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setRejecting(false)}>Cancel</Button>
              <Button danger type="primary" loading={saving} onClick={() => submit("REJECTED")}>
                Confirm Rejection
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picker — when the user has more than one active clearance form to open
// ---------------------------------------------------------------------------

function CandidatePicker({ candidates, onSelect }) {
  return (
    <Card bordered title="Your clearance forms">
      <List
        itemLayout="horizontal"
        dataSource={candidates}
        renderItem={({ detail, myRole }) => {
          const employee = detail.employee;
          const section = detail[myRole.clearanceKey];
          const status = section?.status || "PENDING";
          return (
            <List.Item
              className="candidate-row"
              onClick={() => onSelect(detail.id)}
              actions={[<Tag color={CLEARANCE_STATUS_META[status].color}>{CLEARANCE_STATUS_META[status].label}</Tag>]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar src={employee?.photo} shape="square">
                    {!employee?.photo && initials(employee)}
                  </Avatar>
                }
                title={displayName(employee) || "Unnamed employee"}
                description={`Your action: ${myRole.label}`}
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ExitClearanceApprovalPage({ exitProcessId: exitProcessIdProp } = {}) {
  const { authData, backUrl } = useContext(AuthContext);
  const currentEmployeeId = useCurrentEmployeeId(authData);

  const api = useMemo(() => axios.create({ baseURL: backUrl }), [backUrl]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${authData?.access}` }), [authData?.access]);

  const [phase, setPhase] = useState("loading"); // loading | picking | detail | empty | error
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(exitProcessIdProp || null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id || !authData?.access) return;
      setPhase("loading");
      setError(null);
      try {
        const res = await api.get(EXIT_PROCESS_DETAIL_PATH(id), { headers });
        setDetail(res.data);
        setSelectedId(id);
        setPhase("detail");
      } catch (err) {
        console.error("Error loading exit process detail:", err);
        setError(err.response?.data?.detail || "Could not load the clearance form.");
        setPhase("error");
      }
    },
    [api, headers, authData?.access]
  );

  const discover = useCallback(async () => {
    if (!authData?.access) return;
    setPhase("loading");
    setError(null);
    try {
      const listRes = await api.get(EXIT_PROCESS_LIST_PATH, { params: { is_active: true }, headers });
      const list = Array.isArray(listRes.data) ? listRes.data : listRes.data.results || [];

      if (list.length === 0) {
        setCandidates([]);
        setPhase("empty");
        return;
      }

      // List serializer is lightweight; pull full detail per item to reliably
      // resolve which role (if any) the current user is the approver for.
      // (For larger volumes, add ?approver=<id> to ExitProcessListCreateView.)
      const details = await Promise.all(
        list.map((item) =>
          api
            .get(EXIT_PROCESS_DETAIL_PATH(item.id), { headers })
            .then((r) => r.data)
            .catch(() => null)
        )
      );

      const mine = details
        .filter(Boolean)
        .map((d) => ({ detail: d, myRole: resolveMyRole(d, currentEmployeeId) }))
        .filter(({ myRole }) => myRole);

      setCandidates(mine);

      if (mine.length === 0) {
        setPhase("empty");
      } else if (mine.length === 1) {
        setDetail(mine[0].detail);
        setSelectedId(mine[0].detail.id);
        setPhase("detail");
      } else {
        setPhase("picking");
      }
    } catch (err) {
      console.error("Error discovering exit processes:", err);
      setError(err.response?.data?.detail || "Could not load your clearance items.");
      setPhase("error");
    }
  }, [api, headers, authData?.access, currentEmployeeId]);

  useEffect(() => {
    if (!authData?.access) return;
    if (exitProcessIdProp) {
      loadDetail(exitProcessIdProp);
    } else if (!currentEmployeeId) {
      setError(
        "Couldn't determine your employee record from the session, so pending approvals can't be looked up. Check the AuthContext employee id."
      );
      setPhase("error");
    } else {
      discover();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.access, exitProcessIdProp, currentEmployeeId]);

  const myRole = useMemo(() => resolveMyRole(detail, currentEmployeeId), [detail, currentEmployeeId]);

  // Department Head's handover picker is scoped to the resigning employee's
  // own department, same pool as NocFormpage.jsx used for that role.
  useEffect(() => {
    if (!authData?.access || myRole?.key !== "department_head" || !detail?.employee?.department) {
      setEmployees([]);
      return;
    }
    let cancelled = false;
    api
      .get(EMPLOYEES_PATH, { params: { department: detail.employee.department }, headers })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setEmployees(list.filter((e) => e.id !== detail.employee.id));
      })
      .catch((err) => console.error("Error fetching department employees:", err));
    return () => {
      cancelled = true;
    };
  }, [api, headers, authData?.access, myRole?.key, detail?.employee?.department, detail?.employee?.id]);

  const showBackToList = !exitProcessIdProp && candidates.length > 1 && phase === "detail";
  const employee = detail?.employee;

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2f5d50", borderRadius: 10 } }}>
      <Layout className="approval-page">
        {phase !== "detail" && (
          <div className="approval-header">
            <Text className="approval-eyebrow">Exit Clearance</Text>
            <Title level={3} className="approval-title">My Approvals</Title>
          </div>
        )}

        {showBackToList && (
          <Button type="link" className="back-to-list-btn" icon={<ArrowLeftOutlined />} onClick={() => setPhase("picking")}>
            Back to all pending approvals
          </Button>
        )}

        {phase === "loading" && (
          <Card bordered><Skeleton active paragraph={{ rows: 6 }} /></Card>
        )}

        {phase === "error" && (
          <Alert type="error" showIcon message="Couldn't load clearance data" description={error} />
        )}

        {phase === "empty" && (
          <Card bordered><Empty description="You have no pending exit clearance approvals right now." /></Card>
        )}

        {phase === "picking" && (
          <CandidatePicker candidates={candidates} onSelect={loadDetail} />
        )}

        {phase === "detail" && detail && myRole && (
          <ClearanceFormPaper
            role={myRole}
            section={detail[myRole.clearanceKey]}
            exitProcessId={selectedId}
            employee={employee}
            resignationDate={detail.resignation_date}
            lastWorkingDay={detail.last_working_day}
            employees={employees}
            api={api}
            headers={headers}
            currentEmployee={detail[myRole.clearanceKey]?.approver}
            onSaved={() => loadDetail(selectedId)}
          />
        )}

        {phase === "detail" && detail && !myRole && (
          <Card bordered>
            <Empty description="You're not an assigned approver on this exit process." />
          </Card>
        )}
      </Layout>
    </ConfigProvider>
  );
}